export class Vector {
    x: number;
    y: number;

    constructor(x: number, y: number) {
        this.x = x;
        this.y = y;
    }

    set(x: number, y: number): this {
        this.x = x;
        this.y = y;
        return this;
    }

    add(v: Vector): this {
        this.x += v.x;
        this.y += v.y;
        return this;
    }

    sub(v: Vector): this {
        this.x -= v.x;
        this.y -= v.y;
        return this;
    }

    mul(s: number): this {
        this.x *= s;
        this.y *= s;
        return this;
    }

    div(s: number): this {
        if (s === 0) {
            throw new RangeError('Vector.div: division by zero');
        }
        this.x /= s;
        this.y /= s;
        return this;
    }

    mag(): number {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    }

    normalize(): this {
        const m = this.mag();
        if (m !== 0) {
            this.div(m);
        }
        return this;
    }

    angle(): number {
        return Math.atan2(this.y, this.x);
    }

    setMag(m: number): this {
        const a = this.angle();
        this.x = m * Math.cos(a);
        this.y = m * Math.sin(a);
        return this;
    }

    setAngle(a: number): this {
        const m = this.mag();
        this.x = m * Math.cos(a);
        this.y = m * Math.sin(a);
        return this;
    }

    rotate(a: number): this {
        this.setAngle(this.angle() + a);
        return this;
    }

    limit(l: number): this {
        if (this.mag() > l) {
            this.setMag(l);
        }
        return this;
    }

    angleBetween(v: Vector): number {
        return this.angle() - v.angle();
    }

    dot(v: Vector): number {
        return this.x * v.x + this.y * v.y;
    }

    lerp(v: Vector, amt: number): this {
        this.x += (v.x - this.x) * amt;
        this.y += (v.y - this.y) * amt;
        return this;
    }

    dist(v: Vector): number {
        const dx = this.x - v.x;
        const dy = this.y - v.y;
        return Math.sqrt(dx * dx + dy * dy);
    }

    copy(): Vector {
        return new Vector(this.x, this.y);
    }
}
